import React from 'react';
import { motion } from 'framer-motion';
export interface ReceiptItem {
  name: string;
  price: number;
  quantity?: number;
}
export interface ReceiptVisualProps {
  items: ReceiptItem[];
  storeName?: string;
  showTotal?: boolean;
  totalBudget?: number;
  label?: string;
  mode?: 'stimulus' | 'support';
  /** Optional unit for the price, e.g. 'st', 'kg', 'liter'. Defaults to 'st' */
  priceUnit?: string;
}
function formatEuro(cents: number): string {
  const euros = cents / 100;
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(euros);
}
function formatEuroFromEuros(euros: number): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(euros);
}
/** Detect whether prices are in cents (>10 and integer) or euros */
function isCents(items: ReceiptItem[]): boolean {
  return items.every(i => Number.isInteger(i.price) && Math.abs(i.price) >= 10);
}
export function ReceiptVisual({
  items,
  storeName,
  showTotal = true,
  totalBudget,
  label,
  mode = 'support',
  priceUnit = 'st'
}: ReceiptVisualProps) {
  const validItems = (items || []).filter(i => i.name && typeof i.price === 'number' && Number.isFinite(i.price));
  if (validItems.length === 0) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-14149" data-mp="2gn94dbqk1pop">
        <p className="text-red-600 text-sm font-medium" data-id="element-14150" data-mp="2cle50cs6pmdk">
          Geen producten op de kassabon
        </p>
      </div>;
  }
  const useCents = isCents(validItems);
  const fmt = useCents ? formatEuro : formatEuroFromEuros;
  const lineItems = validItems.map(item => {
    const qty = item.quantity ?? 1;
    const lineTotal = item.price * qty;
    return {
      ...item,
      quantity: qty,
      lineTotal
    };
  });
  const subtotal = lineItems.reduce((sum, i) => sum + i.lineTotal, 0);
  const budgetVal = totalBudget ?? 0;
  const change = budgetVal > 0 ? budgetVal - subtotal : 0;
  // ─── Stimulus mode: compact receipt ──────────────────────────────────
  if (mode === 'stimulus') {
    return <div className="bg-amber-50/80 rounded-xl border border-amber-200 overflow-hidden max-w-xs mx-auto" data-id="element-14151" data-mp="35o6tmqsnybcf">
        {/* Header */}
        <div className="bg-amber-100 px-4 py-2.5 border-b border-amber-200 text-center" data-id="element-14152" data-mp="39oh0s6s6z7sv">
          <span className="text-xs font-bold text-amber-900 tracking-wide uppercase" data-id="element-14153" data-mp="2vphl3omnq38u">
            {storeName || '🧾 Kassabon'}
          </span>
        </div>

        {/* Items */}
        <div className="px-4 py-3 space-y-1.5" data-id="element-14154" data-mp="2mpcfnk10xexb">
          {lineItems.map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -8
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: i * 0.06,
          duration: 0.25
        }} className="flex items-baseline justify-between gap-2 text-sm" data-id="element-14155" data-mp="2mrfgkz3pssgt" data-mpi={i}>
            
              <div className="flex items-baseline gap-1.5 min-w-0" data-id="element-14156" data-mp="29hbvhb1xxl83">
                <span className="text-amber-800 truncate" data-id="element-14157" data-mp="29hln132mw0fw">{item.name}</span>
                {item.quantity > 1 && <span className="text-amber-500 text-xs flex-shrink-0" data-id="element-14158" data-mp="25hbfhlz711yx">
                    ×{item.quantity}
                  </span>}
              </div>
              <span className="text-amber-900 font-mono text-xs flex-shrink-0 tabular-nums" data-id="element-14159" data-mp="2efwvb9da62w0">
                {fmt(item.lineTotal)}
              </span>
            </motion.div>)}
        </div>

        {/* Total */}
        {showTotal && <div className="border-t border-dashed border-amber-300 px-4 py-2.5 flex justify-between items-baseline" data-id="element-14160" data-mp="2o5gg4kkk06ti">
            <span className="text-sm font-bold text-amber-900" data-id="element-14161" data-mp="325hjq7s7dsco">Totaal</span>
            <span className="text-sm font-bold font-mono text-amber-900 tabular-nums" data-id="element-14162" data-mp="2x7zil3kfghf8">
              {fmt(subtotal)}
            </span>
          </div>}

        {label && <div className="px-4 pb-2.5" data-id="element-14163" data-mp="12swyxmi27tk8">
            <p className="text-xs text-amber-600 text-center" data-id="element-14164" data-mp="12u6xsdwql5rx">{label}</p>
          </div>}
      </div>;
  }
  // ─── Support mode: educational receipt with breakdown ────────────────
  return <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200 shadow-sm overflow-hidden max-w-sm mx-auto" data-id="element-14165" data-mp="198dwe0m6d8wq">
      {/* Store header */}
      <div className="bg-amber-100 px-5 py-3 border-b border-amber-200 text-center" data-id="element-14166" data-mp="1j7bjcz3ycudm">
        <div className="text-lg font-bold text-amber-900" data-id="element-14167" data-mp="1j4z0mjqdefzi">
          {storeName || '🧾 Kassabon'}
        </div>
        {label && <p className="text-xs text-amber-600 mt-0.5" data-id="element-14168" data-mp="qie83fy5rxud">{label}</p>}
      </div>

      {/* Receipt body */}
      <div className="px-5 py-4" data-id="element-14169" data-mp="vh8pqeke3gcw">
        {/* Line items */}
        <div className="space-y-2 mb-4" data-id="element-14170" data-mp="vfnxivljs9i7">
          {lineItems.map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.08,
          duration: 0.3
        }} className="flex items-center justify-between gap-3" data-id="element-14171" data-mp="1098uqlfbgzgc" data-mpi={i}>
            
              <div className="flex items-center gap-2 min-w-0" data-id="element-14172" data-mp="7c8g01yws1a">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" data-id="element-14173" data-mp="3rf805aq68rl7" />
                <span className="text-sm text-amber-900 truncate" data-id="element-14174" data-mp="3mfvaki7x16ex">
                  {item.name}
                </span>
                {item.quantity > 1 && <span className="text-xs text-amber-500 flex-shrink-0" data-id="element-14175" data-mp="3hfqweyuhcnrv">
                    ×{item.quantity}
                  </span>}
              </div>
              <div className="flex items-baseline gap-2 flex-shrink-0" data-id="element-14176" data-mp="38ogtigxkjfai">
                {item.quantity > 1 && <span className="text-xs text-amber-400" data-id="element-14177" data-mp="33pdlcnmaksyb">
                    {fmt(item.price)}/{priceUnit}
                  </span>}
                <span className="text-sm font-semibold font-mono text-amber-900 tabular-nums" data-id="element-14178" data-mp="2yr2cog7qgoho">
                  {fmt(item.lineTotal)}
                </span>
              </div>
            </motion.div>)}
        </div>

        {/* Dashed separator */}
        <div className="border-t-2 border-dashed border-amber-300 my-3" data-id="element-14179" data-mp="2prpb09pup47i" />

        {/* Subtotal */}
        {showTotal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: lineItems.length * 0.08 + 0.1
      }} className="flex justify-between items-baseline mb-2" data-id="element-14180" data-mp="2f6txda14xqh8">
          
            <span className="text-sm font-bold text-amber-900" data-id="element-14181" data-mp="2b245rxdiey24">Totaal</span>
            <span className="text-base font-bold font-mono text-amber-900 tabular-nums" data-id="element-14182" data-mp="2ysqc9k7v8aa0">
              {fmt(subtotal)}
            </span>
          </motion.div>}

        {/* Budget & change */}
        {budgetVal > 0 && <>
            <div className="flex justify-between items-baseline text-sm text-amber-700 mb-1" data-id="element-14183" data-mp="2p3gje2576nku">
              <span data-id="element-14184" data-mp="2u0ykx7gvy0it">Betaald</span>
              <span className="font-mono tabular-nums" data-id="element-14185" data-mp="2u06wcglph321">{fmt(budgetVal)}</span>
            </div>
            <div className={`flex justify-between items-baseline text-sm font-bold ${change >= 0 ? 'text-emerald-700' : 'text-amber-900'}`} data-id="element-14186" data-mp="3hprl5g4jiuj8">
            
              <span data-id="element-14187" data-mp="3vwcpkttm2i2t">{change >= 0 ? 'Wisselgeld' : 'Tekort'}</span>
              <span className="font-mono tabular-nums" data-id="element-14188" data-mp="3vurtgzrner2u">
                {fmt(Math.abs(change))}
              </span>
            </div>
          </>}
      </div>

      {/* Educational tip */}
      <div className="bg-white/70 px-5 py-3 border-t border-amber-100" data-id="element-14189" data-mp="9gi70w7a3t4p">
        <div className="flex items-start gap-2.5" data-id="element-14190" data-mp="4j8dp5grqxbl">
          <span className="text-amber-500 text-sm mt-0.5" data-id="element-14191" data-mp="3cy0g16ysf66p">💡</span>
          <p className="text-xs text-amber-800 leading-relaxed" data-id="element-14192" data-mp="3cya6swrnphdg">
            <strong data-id="element-14193" data-mp="38x6tagx2zxfe">Tip:</strong> Tel de euro's en centen apart op. Komen de
            centen boven de 100? Dan gaat er 1 euro over.
          </p>
        </div>
      </div>
    </div>;
}